import type { FC, ReactNode } from 'react'
import type { CategoryRef } from '@/types'

import Link from 'next/link'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'
import { cn } from '@/lib/utils'
import { useShop } from '@/components/providers/shop-provider'
import { Logo } from '@/components/generals/logo'
import { Sheet } from '@/components/generals/sheet'
import { WhatsAppLink } from '@/components/shop/whatsapp-link'
import { buildQuestionMessage } from '@/lib/whatsapp'
import { NAV_LINKS } from '@/constants/site'

import {
    ChevronDown,
    Heart,
    Menu,
    Search,
    ShoppingBag,
    X
} from 'lucide-react'

interface SiteHeaderProps {
    categories: Array<CategoryRef>
    overlay?: boolean
}

const CountBadge: FC<{ count: number }> = ({ count }): ReactNode => {
    if (count <= 0) return null

    return (
        <span className='absolute -right-0.5 -top-0.5 grid min-w-[18px] place-items-center rounded-full bg-blush-500 px-1 text-[10px] font-bold leading-[18px] text-white ring-2 ring-cream-50'>
            {count > 99 ? '99+' : count}
        </span>
    )
}

export const SiteHeader: FC<SiteHeaderProps> = ({ categories, overlay = false }): ReactNode => {
    const router = useRouter()
    const { cartCount, wishlist, hydrated, openCart, openSearch } = useShop()
    const [scrolled, setScrolled] = useState(false)
    const [menuOpen, setMenuOpen] = useState(false)

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24)

        onScroll()
        window.addEventListener('scroll', onScroll, { passive: true })

        return () => window.removeEventListener('scroll', onScroll)
    }, [])

    useEffect(() => {
        const close = () => setMenuOpen(false)

        router.events.on('routeChangeStart', close)

        return () => router.events.off('routeChangeStart', close)
    }, [router.events])

    // "/" or Ctrl/Cmd + K opens search from anywhere.
    useEffect(() => {
        const onKey = (event: KeyboardEvent) => {
            const target = event.target as HTMLElement | null
            const typing = target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)

            if ((event.key === 'k' && (event.metaKey || event.ctrlKey)) || (event.key === '/' && !typing)) {
                event.preventDefault()
                openSearch()
            }
        }

        window.addEventListener('keydown', onKey)

        return () => window.removeEventListener('keydown', onKey)
    }, [openSearch])

    const solid = !overlay || scrolled
    const wishlistCount = hydrated ? wishlist.length : 0
    const bagCount = hydrated ? cartCount : 0

    const isActive = (href: string) => {
        const [path, query] = href.split('?')

        if (query) return router.asPath === href
        if (path === '/') return router.pathname === '/'

        return router.pathname.startsWith(path) && !router.asPath.includes('category=')
    }

    return (
        <>
            <a href='#main' className='sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-full focus:bg-cocoa-900 focus:px-4 focus:py-2 focus:text-cream-50'>
                Skip to content
            </a>
            <header
                className={cn(
                    'fixed inset-x-0 top-0 z-50 transition-all duration-500',
                    solid ? 'border-b border-border/70 bg-cream-50/85 shadow-[0_10px_30px_-24px_rgba(58,40,30,0.5)] backdrop-blur-xl' : 'bg-transparent'
                )}
            >
                <div className='mx-auto flex h-[72px] max-w-7xl items-center justify-between gap-4 px-5 sm:px-8 lg:h-[84px]'>
                    <Link href='/' aria-label='Crochet World home' className='shrink-0 rounded-xl'>
                        <Logo />
                    </Link>

                    <nav aria-label='Main' className='hidden items-center gap-1 lg:flex'>
                        {NAV_LINKS.slice(0, 2).map(link => (
                            <Link
                                key={link.href}
                                href={link.href}
                                className={cn(
                                    'rounded-full px-4 py-2 text-sm font-semibold transition-colors',
                                    isActive(link.href) ? 'bg-white/80 text-cocoa-900 shadow-sm' : 'text-cocoa-700 hover:bg-white/60 hover:text-cocoa-900'
                                )}
                            >
                                {link.label}
                            </Link>
                        ))}

                        {categories.length > 0 && (
                            <div className='group relative'>
                                <button
                                    type='button'
                                    aria-haspopup='true'
                                    className='inline-flex items-center gap-1 rounded-full px-4 py-2 text-sm font-semibold text-cocoa-700 transition-colors hover:bg-white/60 hover:text-cocoa-900'
                                >
                                    Collections
                                    <ChevronDown className='size-4 transition-transform group-hover:rotate-180 group-focus-within:rotate-180' />
                                </button>
                                <div className='invisible absolute left-1/2 top-full w-[300px] -translate-x-1/2 pt-3 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100'>
                                    <ul className='rounded-3xl border border-border bg-cream-50 p-2 shadow-[0_24px_60px_-28px_rgba(58,40,30,0.55)]'>
                                        {categories.map(category => (
                                            <li key={category.id}>
                                                <Link
                                                    href={`/shop?category=${category.slug}`}
                                                    className='block rounded-2xl px-4 py-2.5 text-sm font-semibold text-cocoa-700 transition-colors hover:bg-blush-50 hover:text-cocoa-900'
                                                >
                                                    <span data-mdlfy-cms={`categories:${category.id}:name`}>
                                                        {category.name}
                                                    </span>
                                                </Link>
                                            </li>
                                        ))}
                                        <li className='mt-1 border-t border-border pt-1'>
                                            <Link href='/shop' className='block rounded-2xl px-4 py-2.5 text-sm font-semibold text-blush-600 hover:bg-blush-50'>
                                                View every doll
                                            </Link>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        )}

                        {NAV_LINKS.slice(2).map(link => (
                            <Link
                                key={link.href}
                                href={link.href}
                                className={cn(
                                    'rounded-full px-4 py-2 text-sm font-semibold transition-colors',
                                    isActive(link.href) ? 'bg-white/80 text-cocoa-900 shadow-sm' : 'text-cocoa-700 hover:bg-white/60 hover:text-cocoa-900'
                                )}
                            >
                                {link.label}
                            </Link>
                        ))}
                    </nav>

                    <div className='flex items-center gap-1 sm:gap-1.5'>
                        <button
                            type='button'
                            onClick={openSearch}
                            aria-label='Search dolls'
                            className='grid size-11 place-items-center rounded-full text-cocoa-800 transition hover:bg-white/70'
                        >
                            <Search className='size-5' />
                        </button>
                        <Link
                            href='/wishlist'
                            aria-label={`Wishlist${wishlistCount ? `, ${wishlistCount} saved` : ''}`}
                            className='relative grid size-11 place-items-center rounded-full text-cocoa-800 transition hover:bg-white/70'
                        >
                            <Heart className='size-5' />
                            <CountBadge count={wishlistCount} />
                        </Link>
                        <button
                            type='button'
                            onClick={openCart}
                            aria-label={`Cart${bagCount ? `, ${bagCount} items` : ''}`}
                            className='relative grid size-11 place-items-center rounded-full text-cocoa-800 transition hover:bg-white/70'
                        >
                            <ShoppingBag className='size-5' />
                            <CountBadge count={bagCount} />
                        </button>
                        <button
                            type='button'
                            onClick={() => setMenuOpen(true)}
                            aria-label='Open menu'
                            className='grid size-11 place-items-center rounded-full text-cocoa-800 transition hover:bg-white/70 lg:hidden'
                        >
                            <Menu className='size-5' />
                        </button>
                    </div>
                </div>
            </header>

            <Sheet open={menuOpen} onClose={() => setMenuOpen(false)} labelledBy='mobile-menu-title' side='left'>
                <div className='flex items-center justify-between border-b border-border px-5 py-4'>
                    <p id='mobile-menu-title' className='sr-only'>
                        Menu
                    </p>
                    <Logo />
                    <button
                        type='button'
                        onClick={() => setMenuOpen(false)}
                        aria-label='Close menu'
                        className='grid size-10 place-items-center rounded-full text-cocoa-800 hover:bg-muted'
                    >
                        <X className='size-5' />
                    </button>
                </div>
                <nav aria-label='Mobile' className='flex-1 overflow-y-auto px-3 py-4'>
                    <ul className='space-y-1'>
                        {NAV_LINKS.map(link => (
                            <li key={link.href}>
                                <Link href={link.href} className='block rounded-2xl px-4 py-3 font-heading text-xl text-cocoa-900 transition-colors hover:bg-blush-50'>
                                    {link.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                    {categories.length > 0 && (
                        <>
                            <p className='mt-6 px-4 text-xs font-bold uppercase tracking-[0.22em] text-cocoa-500'>
                                Collections
                            </p>
                            <ul className='mt-2 space-y-0.5'>
                                {categories.map(category => (
                                    <li key={category.id}>
                                        <Link href={`/shop?category=${category.slug}`} className='block rounded-2xl px-4 py-2.5 text-[15px] font-semibold text-cocoa-700 transition-colors hover:bg-blush-50'>
                                            <span data-mdlfy-cms={`categories:${category.id}:name`}>
                                                {category.name}
                                            </span>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </>
                    )}
                </nav>
                <div className='border-t border-border p-5'>
                    <WhatsAppLink message={buildQuestionMessage} className='w-full'>
                        Chat with us on WhatsApp
                    </WhatsAppLink>
                </div>
            </Sheet>
        </>
    )
}
